import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { InvokeAPI } from "../../apis/API";
import { getQuestionMgmtBase } from "../../config/apiBase";
import AsyncSection from "../../components/common/AsyncSection";
import { useAsyncAction } from "../../components/common/useAsyncAction";

const QAStats = () => {
  const { run: runStatsFetch, loading: statsLoading } = useAsyncAction();
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    Classes: [],
    Qclass: "",
    Subjects: [],
    Qsubject: "",
    TotalQuestions: 0,
    CategoryLevelStats: [],
    CategoryTypeStats: [],
  });

  const fetchStats = async (qclass, qsubject) => {
    setError("");
    const info = await InvokeAPI(
      `${getQuestionMgmtBase()}/v1/question/qastats`,
      { Qclass: qclass || "", Qsubject: qsubject || "" },
      "POST",
      {},
      false
    );
    if (info.internal || info.othererror || info.retry) {
      setError("Unable to load QA stats right now.");
      return;
    }
    setStats({
      Classes: info.response.Classes || [],
      Qclass: info.response.Qclass || "",
      Subjects: info.response.Subjects || [],
      Qsubject: info.response.Qsubject || "",
      TotalQuestions: info.response.TotalQuestions || 0,
      CategoryLevelStats: info.response.CategoryLevelStats || [],
      CategoryTypeStats: info.response.CategoryTypeStats || [],
    });
  };

  useEffect(() => {
    runStatsFetch(async () => {
      await fetchStats("", "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClassChange = (event) => {
    runStatsFetch(async () => {
      await fetchStats(event.target.value, "");
    });
  };

  const handleSubjectChange = (event) => {
    runStatsFetch(async () => {
      await fetchStats(stats.Qclass, event.target.value);
    });
  };

  const categoryTypeRows = useMemo(() => {
    return (stats.CategoryTypeStats || []).map((row, index) => ({
      id: `${row.Qcategory}-${row.Qtype}-${index}`,
      category: row.Qcategory,
      qtype: row.Qtype,
      count: row.Count,
    }));
  }, [stats.CategoryTypeStats]);

  return (
    <Box sx={{ paddingBottom: "80px" }}>
      <Stack spacing={3}>
        <Typography variant="h5">QA Stats</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="qa-stats-class-label">Class</InputLabel>
            <Select
              labelId="qa-stats-class-label"
              value={stats.Qclass || ""}
              label="Class"
              onChange={handleClassChange}
              disabled={statsLoading}
            >
              {(stats.Classes || []).map((cls) => (
                <MenuItem key={cls} value={cls}>
                  {cls}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="qa-stats-subject-label">Subject</InputLabel>
            <Select
              labelId="qa-stats-subject-label"
              value={stats.Qsubject || ""}
              label="Subject"
              onChange={handleSubjectChange}
              disabled={statsLoading}
            >
              {(stats.Subjects || []).map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <AsyncSection loading={statsLoading} minHeight={280}>
        {error !== "" && <Alert severity="error">{error}</Alert>}
        {error === "" && stats.Qclass === "" && (
          <Alert severity="info">No class data found in questions.</Alert>
        )}
        {error === "" && stats.Qclass !== "" && stats.Qsubject === "" && (
          <Alert severity="info">No subject data found for selected class.</Alert>
        )}

        {error === "" && stats.Qclass !== "" && stats.Qsubject !== "" && (
          <>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">Total Questions</Typography>
              <Typography variant="h4">{stats.TotalQuestions}</Typography>
            </Paper>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Easy (L1)</TableCell>
                    <TableCell align="right">Intermediate (L2)</TableCell>
                    <TableCell align="right">Advanced (L3)</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats.CategoryLevelStats || []).map((row) => (
                    <TableRow key={row.Qcategory}>
                      <TableCell>{row.Qcategory}</TableCell>
                      <TableCell align="right">{row.Easy}</TableCell>
                      <TableCell align="right">{row.Intermediate}</TableCell>
                      <TableCell align="right">{row.Advanced}</TableCell>
                      <TableCell align="right">{row.Total}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>QA Type</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categoryTypeRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.category}</TableCell>
                      <TableCell>{row.qtype}</TableCell>
                      <TableCell align="right">{row.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        </AsyncSection>
      </Stack>
    </Box>
  );
};

export default QAStats;
